// ============================================================
// ConsiliumLoader.tsx
// ============================================================
// Branded loading animation: the ring draws itself, the point
// pops in with a slight overshoot, holds, fades, and loops.
//
// Usage:
//   <ConsiliumLoader />                      // default 120px
//   <ConsiliumLoader size={34} />            // inline, e.g. next to text
//   <ConsiliumLoader size={48} label="Loading your practice…" />
//
// Uses CSS animations (no JS timers), respects prefers-reduced-motion.
// ============================================================

interface ConsiliumLoaderProps {
  /** Pixel size of the square mark. Default 120. */
  size?: number;
  /** Optional text shown to the right of the mark. */
  label?: string;
  /** Mark color. Defaults to the brand gold. */
  color?: string;
}

export function ConsiliumLoader({ size = 120, label, color = '#d4a574' }: ConsiliumLoaderProps) {
  // Scoped keyframes — injected once. Safe to render multiple loaders;
  // duplicate <style> with the same content is harmless.
  const css = `
    @keyframes consRingDraw {
      0%     { stroke-dashoffset: 100; opacity: 1; }
      40%    { stroke-dashoffset: 0;   opacity: 1; }
      80%    { stroke-dashoffset: 0;   opacity: 1; }
      93%    { stroke-dashoffset: 0;   opacity: 0; }
      93.01% { stroke-dashoffset: 100; opacity: 0; }
      100%   { stroke-dashoffset: 100; opacity: 0; }
    }
    @keyframes consDotPop {
      0%   { transform: scale(0); opacity: 0; }
      40%  { transform: scale(0); opacity: 0; }
      48%  { transform: scale(1.28); opacity: 1; }
      55%  { transform: scale(1); opacity: 1; }
      80%  { transform: scale(1); opacity: 1; }
      93%  { transform: scale(1); opacity: 0; }
      100% { transform: scale(0); opacity: 0; }
    }
    .cons-ring { stroke-dasharray: 100; animation: consRingDraw 2.4s ease-in-out infinite; }
    .cons-dot  { transform-box: fill-box; transform-origin: center; animation: consDotPop 2.4s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      /* Don't animate for users who asked not to — show the finished mark. */
      .cons-ring { animation: none; stroke-dashoffset: 0; opacity: 1; }
      .cons-dot  { animation: none; transform: scale(1); opacity: 1; }
    }
  `;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
      <style>{css}</style>
      <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={label || 'Loading'}>
        <path
          className="cons-ring"
          pathLength={100}
          d="M78.19 39.74 A30 30 0 1 0 60.26 78.19"
          fill="none"
          stroke={color}
          strokeWidth={8.5}
          strokeLinecap="round"
        />
        <circle className="cons-dot" cx={71.75} cy={58.25} r={5.6} fill={color} />
      </svg>
      {label && (
        <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 15, color: '#a8a29e' }}>
          {label}
        </span>
      )}
    </div>
  );
}
